import { useState, useEffect } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { Palette, X, RotateCcw } from "lucide-react";

const LAYOUT_KEY = "carelogic_flush_center";

const DEFAULT_THEME: Record<string, string> = {
  "--color-primary": "#7B6DAF",
  "--color-primary-light": "#EDE8F5",
  "--color-gold": "#D4A843",
  "--color-text": "#1B2A4A",
  "--color-bg": "#FAFAFA",
  "--color-surface": "#FFFFFF",
  "--color-border": "#E8E5F0",
  "--color-sidebar-bg": "#FFFFFF",
  "--color-banner": "#1B2A4A",
};

const LABELS: Record<string, string> = {
  "--color-primary": "Primary (buttons, links)",
  "--color-primary-light": "Primary light (backgrounds)",
  "--color-gold": "Gold (accents, rates)",
  "--color-text": "Text color",
  "--color-bg": "Page background",
  "--color-surface": "Card background",
  "--color-border": "Borders",
  "--color-sidebar-bg": "Sidebar background",
  "--color-banner": "Banner background",
};

const STORAGE_KEY = "carelogic_theme_overrides";
const GLOBAL_THEME_KEY = "carelogic_global_theme";

// "Default" is no remap — same editorial tokens the app ships with. Kept as
// a distinct option so users have a clear "reset to defaults" target; if a
// dedicated non-editorial skin ever ships, its id should replace "classic".
const GLOBAL_THEMES: Array<{ id: "classic" | "concierge" | "shift_report"; label: string }> = [
  { id: "classic", label: "Default" },
  { id: "concierge", label: "Concierge" },
  { id: "shift_report", label: "Field View" },
];

/** Check if a hex color is "dark" (needs light text on top) */
function isDark(hex: string): boolean {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

export default function ThemeEditor({ isFounder }: { isFounder: boolean }) {
  // Desktop-only founder tool: on mobile its fixed bottom-left trigger sat on
  // top of calendar legends and list rows (FINDING-002, /design-review
  // 2026-06-11). Theme design work happens on desktop anyway.
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [flushCenter, setFlushCenter] = useState(() => localStorage.getItem(LAYOUT_KEY) === "true");
  const [flushLeft, setFlushLeft] = useState(() => localStorage.getItem("carelogic_flush_left") === "true");
  const [flushRight, setFlushRight] = useState(() => localStorage.getItem("carelogic_flush_right") === "true");
  const [globalTheme, setGlobalTheme] = useState<string>(
    () => localStorage.getItem(GLOBAL_THEME_KEY) || ""
  );
  // Apply the theme with a cinematic clip-path wipe originating from the
  // clicked chip. View Transitions API is Chrome/Edge/Safari; Firefox and
  // reduced-motion users fall back to the instant class swap.
  const applyGlobalTheme = (themeId: string, event?: { clientX: number; clientY: number }) => {
    const doUpdate = () => {
      setGlobalTheme(themeId);
      localStorage.setItem(GLOBAL_THEME_KEY, themeId);
      window.dispatchEvent(new Event("carelogic:view-changed"));
    };

    const reduced = typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const startVT: ((cb: () => void) => unknown) | undefined = document.startViewTransition?.bind(document);

    if (reduced || !startVT) {
      doUpdate();
      return;
    }

    const x = event?.clientX ?? window.innerWidth / 2;
    const y = event?.clientY ?? window.innerHeight / 2;
    // Radius = distance from click origin to the farthest viewport corner so
    // the growing circle always just covers the screen.
    const r = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
    document.documentElement.style.setProperty("--theme-wipe-x", `${x}px`);
    document.documentElement.style.setProperty("--theme-wipe-y", `${y}px`);
    document.documentElement.style.setProperty("--theme-wipe-r", `${r}px`);

    startVT(() => doUpdate());
  };
  const [overrides, setOverrides] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Flush center layout toggle
  useEffect(() => {
    document.documentElement.classList.toggle("flush-center", flushCenter);
    document.documentElement.classList.toggle("flush-left", flushLeft);
    document.documentElement.classList.toggle("flush-right", flushRight);
    localStorage.setItem(LAYOUT_KEY, String(flushCenter));
    localStorage.setItem("carelogic_flush_left", String(flushLeft));
    localStorage.setItem("carelogic_flush_right", String(flushRight));
  }, [flushCenter, flushLeft, flushRight]);

  // Track dark mode reactively so theme overrides are cleared when toggling
  const [isDarkMode, setIsDarkMode] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Only set inline styles for overridden values — not defaults
  // This lets the .dark class and :root CSS work normally for non-overridden values
  // In dark mode, clear all inline overrides so .dark CSS variables take precedence
  useEffect(() => {
    // Clear all theme properties first
    for (const key of Object.keys(DEFAULT_THEME)) {
      document.documentElement.style.removeProperty(key);
    }
    document.documentElement.style.removeProperty("--color-logo-care");
    document.documentElement.style.removeProperty("--color-sidebar-text");

    // Only apply overrides in light mode — inline styles would clobber .dark CSS vars
    if (!isDarkMode) {
      for (const [key, value] of Object.entries(overrides) as [string, string][]) {
        document.documentElement.style.setProperty(key, value);
      }

      // Auto-adjust logo and sidebar text color based on sidebar background
      const sidebarBg = overrides["--color-sidebar-bg"];
      if (sidebarBg) {
        if (isDark(sidebarBg)) {
          document.documentElement.style.setProperty("--color-logo-care", "#FFFFFF");
          document.documentElement.style.setProperty("--color-sidebar-text", "#E7E9EA");
        } else {
          document.documentElement.style.setProperty("--color-logo-care", "#1B2A4A");
          document.documentElement.style.setProperty("--color-sidebar-text", "#1B2A4A");
        }
      }
    }

    // Persist
    if (Object.keys(overrides).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [overrides, isDarkMode]);

  if (!isFounder || isMobile) return null;

  const updateColor = (key: string, value: string) => {
    if (value === DEFAULT_THEME[key]) {
      // Remove override if it matches the default
      setOverrides(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setOverrides(prev => ({ ...prev, [key]: value }));
    }
  };

  const resetTheme = () => {
    setOverrides({});
    setFlushCenter(false);
    setFlushLeft(false);
    setFlushRight(false);
  };

  const currentValue = (key: string) => overrides[key] || DEFAULT_THEME[key];

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: "fixed",
          bottom: "calc(64px + env(safe-area-inset-bottom) + 52px)",
          left: 16,
          zIndex: 9999,
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: "var(--color-primary)",
          color: "var(--color-on-primary)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}
        title="Theme Editor"
      >
        <Palette size={18} />
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            bottom: "calc(64px + env(safe-area-inset-bottom) + 100px)",
            left: 16,
            zIndex: 9999,
            width: 280,
            maxHeight: "calc(100dvh - 180px)",
            overflowY: "auto",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            padding: 16,
            fontFamily: "inherit",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)" }}>Theme Editor</span>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}
            >
              <X size={16} color="var(--color-text-muted)" />
            </button>
          </div>

          {/* Global theme preset — one click, applies app-wide */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-muted)", marginBottom: 6 }}>
              Theme
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {GLOBAL_THEMES.map(t => {
                const active = globalTheme === t.id;
                return (
                  <button
                    key={t.id || "classic"}
                    onClick={(e) => applyGlobalTheme(t.id, { clientX: e.clientX, clientY: e.clientY })}
                    style={{
                      fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                      padding: "8px 4px", borderRadius: 6, cursor: "pointer",
                      border: active ? "1px solid var(--color-primary)" : "1px solid var(--color-border)",
                      background: active ? "var(--color-primary)" : "var(--color-surface)",
                      color: active ? "var(--color-on-primary, #fff)" : "var(--color-text)",
                      minHeight: 36,
                      // Prevent "Field View" from wrapping onto two
                      // lines while Concierge / Classic stay single-line.
                      // P3 #56 from 2026-05-20 audit.
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Object.entries(LABELS).map(([key, label]) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="color"
                  value={currentValue(key)}
                  onChange={(e) => updateColor(key, e.target.value)}
                  style={{
                    width: 28,
                    height: 28,
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    cursor: "pointer",
                    padding: 0,
                    background: "none",
                  }}
                />
                <span style={{ fontSize: 11, color: "var(--color-text)", flex: 1 }}>{label}</span>
                {overrides[key] && (
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--color-primary)", flexShrink: 0 }} title="Modified" />
                )}
              </div>
            ))}
          </div>

          {/* Layout toggles */}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>Layout</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {([
                { label: "Flush left column", desc: "Business & Client Overview", on: flushLeft, toggle: () => setFlushLeft(!flushLeft) },
                { label: "Flush center column", desc: "Shift & Care Center", on: flushCenter, toggle: () => setFlushCenter(!flushCenter) },
                { label: "Flush right column", desc: "Insights sidebar", on: flushRight, toggle: () => setFlushRight(!flushRight) },
              ] as const).map(item => (
                <button
                  key={item.label}
                  onClick={item.toggle}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "6px 10px", borderRadius: 8,
                    border: "1px solid var(--color-border)",
                    background: item.on ? "var(--color-primary-light)" : "none",
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text)" }}>{item.label}</div>
                    <div style={{ fontSize: 9, color: "var(--color-text-muted)" }}>{item.desc}</div>
                  </div>
                  <div style={{
                    width: 28, height: 16, borderRadius: 8, position: "relative", flexShrink: 0,
                    background: item.on ? "var(--color-primary)" : "var(--color-border)",
                    transition: "background 0.2s",
                  }}>
                    <div style={{
                      width: 12, height: 12, borderRadius: "50%", background: "white",
                      position: "absolute", top: 2,
                      left: item.on ? 14 : 2,
                      transition: "left 0.2s",
                    }} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={resetTheme}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              width: "100%",
              marginTop: 12,
              padding: 8,
              borderRadius: 8,
              border: "1px solid var(--color-border)",
              background: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--color-text-muted)",
            }}
          >
            <RotateCcw size={12} /> Reset to defaults
          </button>

          {Object.keys(overrides).length > 0 && (
            <div style={{ fontSize: 10, color: "var(--color-primary)", marginTop: 6, textAlign: "center" }}>
              {Object.keys(overrides).length} color{Object.keys(overrides).length !== 1 ? "s" : ""} customized
            </div>
          )}

          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, textAlign: "center" }}>
            Founder only · Changes saved locally
          </div>
        </div>
      )}
    </>
  );
}
